/**
 * en locale — English (US)
 *
 * Extracted from all frontend source files (Issue #18 — i18n).
 * Falls back to pt-BR for any missing key.
 */
export const en: Record<string, string> = {
  // ---------------------------------------------------------------------------
  // common — shared strings used across multiple pages/components
  // ---------------------------------------------------------------------------
  'common.loading': 'Loading...',
  'common.checkSpam': 'If you don\'t receive it within a few minutes, check your spam or junk folder.',
  'common.checkSpamThirdParty': 'If they don\'t receive it within a few minutes, ask them to check their spam or junk folder.',
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.filter': 'Filter',
  'common.from': 'From',
  'common.to': 'To',
  'common.all': 'All',
  'common.type': 'Type',
  'common.status': 'Status',
  'common.origin': 'Origin',
  'common.date': 'Date',
  'common.file': 'File',
  'common.value': 'Value',
  'common.confidence': 'Confidence',
  'common.source': 'Source',
  'common.field': 'Field',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.email': 'Email',
  'common.processing': 'Processing...',
  'common.error': 'Error',
  'common.dash': '—',
  'common.filterPlaceholder': 'Filter...',
  'common.importId': 'Import ID',
  'common.corrected': 'corrected',
  'common.errorPrefix': 'Error: ',

  // Document type labels
  'common.doctype.nfe': 'NF-e',
  'common.doctype.nfse': 'NFS-e',
  'common.doctype.nfcom': 'NFCom',
  'common.doctype.email': 'Email',
  'common.doctype.zip': 'ZIP',
  'common.doctype.unknown': 'Unknown',

  // Legal document labels
  'common.legal.terms': 'Terms of Use',
  'common.legal.privacy': 'Privacy Policy',
  'common.legal.cookies': 'Cookies Policy',
  'common.legal.termsShort': 'Terms',
  'common.legal.privacyShort': 'Privacy',
  'common.legal.cookiesShort': 'Cookies',
  'common.legal.custodia': 'Certificate Custody Terms',
  'common.legal.back': 'Back',

  // ---------------------------------------------------------------------------
  // app — App.tsx
  // ---------------------------------------------------------------------------
  'app.loading': 'Loading...',
  // Issue #558: session restore with retry — text while checking,
  // persistent transient failure (token kept) and manual retry button.
  'app.checking_session': 'Checking session...',
  'app.session_check_failed': 'Could not confirm your session right now — the server may be unavailable. Your session was kept.',
  'app.try_again': 'Try again',

  // ---------------------------------------------------------------------------
  // home — Home.tsx
  // ---------------------------------------------------------------------------
  'home.title': 'Aura Nota',
  'home.subtitle': 'Tool for capturing and structured extraction of Brazilian tax documents (NFS-e, NF-e and telecom).',
  'home.card.upload.title': 'Upload',
  'home.card.upload.desc': 'Receive PDFs, XMLs and document images.',
  'home.card.extraction.title': 'Extraction',
  'home.card.extraction.desc': 'OCR, XML and LLM to automatically read fields.',
  'home.card.review.title': 'Review',
  'home.card.review.desc': 'Human interface to validate and correct data.',
  'home.hero.title': 'Aura Nota — Smart capture and extraction of tax invoices',
  'home.hero.subtitle': 'Automate reading of NFS-e, NFCom and receipts with AI. Extract, validate and export tax fields in seconds.',
  'home.hero.cta.signup': 'Start for free',
  'home.hero.cta.pricing': 'See plans',
  'home.features.title': 'Features',
  'home.features.extraction.title': 'Automatic Extraction',
  'home.features.extraction.desc': 'Tax fields extracted in seconds with AI',
  'home.features.lgpd.title': 'LGPD Compliance',
  'home.features.lgpd.desc': 'Server in the European Union under DPA, encryption in transit and at rest, DSAR and consent built-in',
  'home.features.multilayout.title': 'Multi-layout',
  'home.features.multilayout.desc': 'NFS-e, NFCom, receipts and more',
  'home.features.export.title': 'Structured Export',
  'home.features.export.desc': 'JSON, CSV and integration with your ERP',
  'home.pricing.teaser.title': 'Plans from R$ 0',
  'home.pricing.teaser.desc': 'Start for free and scale as you grow. No credit card required.',
  'home.pricing.teaser.cta': 'See plans',
  'home.footer.login': 'Login',
  'home.footer.signup': 'Sign up',
  'home.footer.pricing': 'Pricing',
  'home.footer.dsar': 'DSAR',
  'home.footer.terms': 'Terms',
  'home.footer.privacy': 'Privacy',
  // Issue #594: public landing sections (visitor).
  'home.howItWorks.title': 'How it works',
  'home.howItWorks.step1.title': 'Send your invoices',
  'home.howItWorks.step1.desc': 'Forward documents to your dedicated capture e-mail or manually upload PDFs, XMLs and images.',
  'home.howItWorks.step2.title': 'Automatic extraction',
  'home.howItWorks.step2.desc': 'XML is read directly; PDFs and images go through OCR and AI to extract the tax fields.',
  'home.howItWorks.step3.title': 'Review safely',
  'home.howItWorks.step3.desc': 'Low-confidence fields go to the review queue — every correction is recorded in the audit trail.',
  'home.howItWorks.step4.title': 'Export to your ERP',
  'home.howItWorks.step4.desc': 'Download structured data in JSON or CSV and send it to your accounting system or accountant.',
  'home.captures.title': 'What we capture',
  'home.captures.nfse.title': 'NFS-e',
  'home.captures.nfse.desc': 'Service invoices in PDF and XML across the many municipal layouts and providers.',
  'home.captures.nfcom.title': 'NFCom / NFT (model 62)',
  'home.captures.nfcom.desc': 'Telecom bills with dedicated templates for the major carriers plus a generic layout.',
  'home.captures.nfe.title': 'NF-e and NFC-e',
  'home.captures.nfe.desc': 'Merchandise invoice and electronic coupon XMLs read directly, no OCR needed.',
  'home.captures.others.title': 'Receipts, ZIP and e-mail',
  'home.captures.others.desc': 'Receipts and scanned documents via OCR/AI, plus attachments inside ZIP files and e-mails (.eml).',
  'home.personas.title': 'Who it is for',
  'home.personas.company.title': 'Companies',
  'home.personas.company.desc': 'Your finance team forwards invoices to the capture e-mail and exports ready data to the ERP or accountant.',
  'home.personas.accounting.title': 'Accounting firms',
  'home.personas.accounting.desc': 'Team plan with up to 5 users, accountant role and documents separated per client.',
  'home.personas.dev.title': 'Developers',
  'home.personas.dev.desc': 'Integrate capture into your system via the API — available on the Enterprise plan, contact us.',
  'home.diff.title': 'Differentiators',
  'home.diff.dedup.title': 'Automatic deduplication',
  'home.diff.dedup.desc': 'The same invoice never enters twice: we detect duplicates by file hash and by the NF access key.',
  'home.diff.audit.title': 'Audit trail',
  'home.diff.audit.desc': 'Every action on a document is recorded — who did it, when and why (LGPD).',
  'home.faq.title': 'Frequently asked questions',
  'home.faq.q1': 'Which document types does Aura Nota process?',
  'home.faq.a1': 'NFS-e (PDF and XML across many municipal layouts), NFCom/NFT model 62 bills, NF-e and NFC-e XML, receipts and other PDFs or images via OCR. ZIP files and e-mails with attachments are also accepted.',
  'home.faq.q2': 'How does the dedicated e-mail capture work?',
  'home.faq.a2': 'Each organization gets an exclusive capture address. Just forward the e-mails with the invoice attachments — they enter the processing queue automatically.',
  'home.faq.q3': 'Do you keep the original files?',
  'home.faq.a3': 'No. The file is processed and discarded; we only keep the extracted data and metadata for your organization\'s configurable retention period (default: 5 years).',
  'home.faq.q4': 'Do I need a credit card to start?',
  'home.faq.a4': 'No. The Free plan includes 10 documents per month and does not require a card. Upgrade when you need more volume or users.',
  'home.faq.q5': 'Can I cancel anytime?',
  'home.faq.a5': 'Yes. Cancellation takes effect at the end of the paid period — no immediate charge and no penalty.',
  'home.faq.q6': 'Where is my data hosted?',
  'home.faq.a6': 'On a server in the European Union (France), under a data processing agreement (DPA) and Standard Contractual Clauses, in compliance with LGPD. Details are in the Privacy Policy.',
  'home.pricing.teaser.ctaSignup': 'Create free account',

  // ---------------------------------------------------------------------------
  // login — Login.tsx
  // ---------------------------------------------------------------------------
  'login.title': 'Sign In',
  'login.subtitle': 'Access your Aura Nota account.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign In',
  'login.submitting': 'Signing in...',
  'login.noAccount': "Don't have an account?",
  'login.signupLink': 'Sign up',
  'login.error.auth': 'Authentication error.',
  'session.expired': 'Your session has expired. Please log in again.',
  'session.loginAgain': 'Log in again',

  // ---------------------------------------------------------------------------
  // layout — Layout.tsx
  // ---------------------------------------------------------------------------
  'layout.brand': 'Aura Nota',
  'layout.nav.home': 'Home',
  'layout.nav.upload': 'Upload',
  'layout.nav.documents': 'Documents',
  'layout.nav.billing': 'Billing',
  'layout.nav.quota': 'Quota',
  'layout.nav.subscription': 'Subscription',
  // Issue #549: 'My account' unified (was 'My Account'/'Account').
  'layout.nav.myAccount': 'My account',
  'layout.nav.team': 'Team',
  'layout.nav.admin': 'Admin',
  // Issue #549: 'Clientes' nav item moved to i18n.
  'layout.nav.clients': 'Customers',
  // Issue #550: "Gestao" nav group (was "Plataforma") — Settings,
  // LGPD Audit, Team, Customers and Municipalities.
  'layout.nav.gestao': 'Management',
  'layout.nav.audit': 'LGPD Audit',
  // Issue #549: "no filter" option of the active-customer selector.
  'layout.clientSelector.all': 'All customers',
  'layout.account': 'My account',
  'layout.logout': 'Logout',
  'layout.login': 'Sign In',
  'layout.footer.terms': 'Terms of Use',
  'layout.footer.privacy': 'Privacy Policy',
  'layout.footer.cookies': 'Cookies Policy',
  'layout.footer.tagline': 'Aura Nota · tax document capture and extraction',

  // ---------------------------------------------------------------------------
  // consent — ConsentGate.tsx
  // ---------------------------------------------------------------------------
  'consent.title': 'LGPD Consent',
  'consent.intro': 'To continue using Aura Nota, you must accept the current version ({version}) of the following legal documents:',
  'consent.acceptLabel': 'I have read and accept the Terms of Use, the Privacy Policy and the Cookies Policy.',
  'consent.submit': 'Accept and continue',
  'consent.submitting': 'Registering...',
  'consent.error.check': 'Error checking consent.',
  'consent.error.mustAccept': 'You must accept the legal documents to continue.',
  'consent.error.register': 'Error registering consent.',

  // ---------------------------------------------------------------------------
  // errorBoundary — ErrorBoundary.tsx
  // ---------------------------------------------------------------------------
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.message': 'An unexpected error occurred. Try reloading the page.',
  'errorBoundary.reload': 'Reload',

  // ---------------------------------------------------------------------------
  // account — Account.tsx
  // ---------------------------------------------------------------------------
  'account.title': 'My account',
  'account.emailLabel': 'Email:',
  'account.statusLabel': 'Status:',
  'account.statusActive': 'Active',
  'account.statusInactive': 'Inactive',
  'account.historyTitle': 'Consent history',
  'account.legalDocsIntro': 'Current legal documents: ',
  'account.loading': 'Loading...',
  'account.error.load': 'Error loading history.',
  'account.empty': 'No consent recorded.',
  'account.col.document': 'Document',
  'account.col.version': 'Version',
  'account.col.accepted': 'Accepted',
  'account.col.date': 'Date',
  'account.col.ip': 'IP',
  // Personal data editing ("My details") + password change.
  'account.profile.title': 'My details',
  'account.profile.name': 'Name',
  'account.profile.namePlaceholder': 'Your full name',
  'account.profile.nameRequired': 'Please enter a valid name.',
  'account.profile.language': 'Language',
  'account.profile.email': 'Email',
  'account.profile.emailNote': 'To change your email, contact support/administrator.',
  'account.profile.save': 'Save',
  'account.profile.saving': 'Saving...',
  'account.profile.saved': 'Details updated successfully.',
  'account.profile.error': 'Error saving details.',
  'account.password.title': 'Change password',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.confirm': 'Confirm new password',
  'account.password.hint': 'Minimum 8 characters, with at least 1 letter and 1 number.',
  'account.password.submit': 'Change password',
  'account.password.submitting': 'Changing...',
  'account.password.success': 'Password changed successfully.',
  'account.password.error': 'Error changing password.',
  'account.password.mismatch': 'Confirmation does not match the new password.',
  'account.password.tooShort': 'The new password must be at least 8 characters.',

  // ---------------------------------------------------------------------------
  // audit — Audit.tsx
  // ---------------------------------------------------------------------------
  'audit.title': 'LGPD Audit',
  'audit.subtitle': 'Log of processing, access and consent events.',
  'audit.exportCsv': 'Export CSV',
  'audit.exportJson': 'Export JSON',
  // Issue #550: async export (authenticated fetch + blob download).
  'audit.exporting': 'Exporting...',
  'audit.error.export': 'Error exporting audit.',
  'audit.compliance.title': 'LGPD Compliance',
  'audit.compliance.pii': 'Anonymized PII: file names and hashes are stored without direct personal data',
  'audit.compliance.retention': 'Retention: audit logs are immutable and maintained according to the retention policy',
  'audit.compliance.dsar': 'DSAR: data subject rights (access, deletion, portability) available via API',
  'audit.filter.eventType': 'Event type',
  'audit.filter.from': 'From',
  'audit.filter.to': 'To',
  'audit.filter.all': 'All',
  'audit.loading': 'Loading...',
  'audit.error.load': 'Error loading audit.',
  'audit.empty': 'No audit events found.',
  'audit.eventCount': '{count} events',
  'audit.col.date': 'Date',
  'audit.col.event': 'Event',
  'audit.col.resource': 'Resource',
  'audit.col.details': 'Details',
  'audit.col.user': 'User',
  // Issue #550: action filter optgroups — actions actually written to
  // audit_log by the backend (see AUDIT_ACTION_GROUPS).
  'audit.group.documents': 'Documents',
  'audit.group.sefaz': 'Tax / SEFAZ',
  'audit.group.certificates': 'Certificates',
  'audit.group.org': 'Organization and users',
  'audit.group.finance': 'Reconciliation and RPA',
  'audit.group.catalogo': 'Catalog',
  'audit.group.other': 'Other',
  // Action labels (filter + table Event column)
  'audit.action.document_upload': 'Document upload',
  'audit.action.document_upload_api_v1': 'API upload',
  'audit.action.document_email_upload': 'Email upload',
  'audit.action.document_upload_duplicate': 'Duplicate upload',
  'audit.action.document_processed': 'Document processed',
  'audit.action.document_field_correction': 'Field correction',
  'audit.action.document_substituted': 'Document substituted',
  'audit.action.document_soft_deleted': 'Deletion (trash)',
  'audit.action.document_deleted': 'Permanent deletion',
  'audit.action.document_restored': 'Document restored',
  'audit.action.document_purged': 'Retention purge',
  'audit.action.sefaz_search': 'SEFAZ query',
  'audit.action.sefaz_batch_search': 'SEFAZ batch query',
  'audit.action.sefaz_batch_upload': 'SEFAZ batch upload',
  'audit.action.sefaz_batch_export': 'SEFAZ batch export',
  'audit.action.nfse_validate': 'NFS-e validation',
  'audit.action.cert_upload': 'Certificate upload',
  'audit.action.cert_view': 'Certificate view',
  'audit.action.cert_rotate': 'Certificate rotation',
  'audit.action.cert_delete': 'Certificate deletion',
  'audit.action.cert_use': 'Certificate use',
  'audit.action.cert_session_open': 'Certificate session opened',
  'audit.action.cert_consent_grant': 'Certificate consent',
  'audit.action.member_activate': 'Member reactivated',
  'audit.action.member_deactivate': 'Member deactivated',
  'audit.action.member_password_reset': 'Member password reset',
  'audit.action.org_switch': 'Organization switch',
  'audit.action.org_settings_update': 'Settings updated',
  'audit.action.impersonation_start': 'Impersonation started',
  'audit.action.impersonation_exit': 'Impersonation ended',
  'audit.action.api_key_created': 'API key created',
  'audit.action.api_key_revoked': 'API key revoked',
  'audit.action.conciliacao_mapping': 'Reconciliation mapping',
  'audit.action.conciliacao_suggest': 'Reconciliation suggestion',
  'audit.action.conciliacao_review': 'Reconciliation review',
  'audit.action.conciliacao_export': 'Reconciliation export',
  'audit.action.rpa_credential_save': 'RPA credential saved',
  'audit.action.rpa_credential_delete': 'RPA credential deleted',
  'audit.action.rpa_run': 'RPA run',
  'audit.action.rpa_operator_notify': 'RPA operator notification',
  'audit.action.catalogo_sugestao_create': 'Catalog suggestion created',
  'audit.action.catalogo_sugestao_review': 'Catalog suggestion reviewed',
  'audit.action.catalogo_municipio_write': 'Municipality catalog updated',
  'audit.action.catalogo_import': 'Catalog import',
  'audit.action.onboarding_step': 'Onboarding step',
  'audit.action.onboarding_completed': 'Onboarding completed',

  // ---------------------------------------------------------------------------
  // documents — Documents.tsx
  // ---------------------------------------------------------------------------
  'documents.title': 'Documents',
  'documents.view.grid': 'Grid',
  'documents.view.cards': 'Cards',
  // Issue #549: active-customer banners (previously hardcoded pt-BR).
  'documents.clientBanner.active': 'Showing documents for customer: {name}',
  'documents.clientBanner.all': 'Showing all customers. Select a customer in the top selector to filter.',
  'documents.export.csv': 'Export CSV',
  'documents.export.excel': 'Export Excel',
  'documents.filter.type': 'Type',
  'documents.filter.status': 'Status',
  'documents.filter.all': 'All',
  'documents.filter.from': 'From',
  'documents.filter.to': 'To',
  'documents.filter.importId': 'Import ID',
  'documents.filter.importIdPlaceholder': 'e.g.: 20260819-001',
  'documents.filter.button': 'Filter',
  'documents.loading': 'Loading...',
  'documents.loadingDetails': 'Loading details...',
  'documents.empty': 'No documents found.',
  'documents.error.load': 'Error loading documents.',
  'documents.error.loadDetail': 'Error loading detail.',
  'documents.error.saveField': 'Error saving field.',
  'documents.error.export': 'Error exporting.',

  // Status filter options
  'documents.status.concluido': 'Completed',
  'documents.status.revisao': 'Review',
  'documents.status.erro': 'Error',
  'documents.status.recebido': 'Received',

  // Fiscal column headers (grid)
  'documents.col.numero': 'Number',
  'documents.col.serie': 'Series',
  'documents.col.prestadorCnpj': 'Provider CNPJ',
  'documents.col.prestadorCnpjEmitente': 'Issuer CNPJ',
  'documents.col.prestador': 'Provider',
  'documents.col.tomadorCnpj': 'Recipient CNPJ',
  'documents.col.tomadorCnpjDestinatario': 'Recipient CNPJ',
  'documents.col.tomador': 'Recipient',
  'documents.col.valorTotal': 'Total Value',
  'documents.col.valorServico': 'Service Value',
  'documents.col.dataEmissao': 'Issue Date',
  'documents.col.municipio': 'Municipality',
  'documents.col.codVerificacao': 'Verification Code',
  'documents.col.descricao': 'Description',
  'documents.col.imPrestador': 'Provider IM',
  'documents.col.docType': 'Document Type',
  'documents.col.importId': 'Import ID',
  'documents.col.origin': 'Origin',

  // Cards view headers
  'documents.cards.importId': 'Import ID',
  'documents.cards.file': 'File',
  'documents.cards.type': 'Type',
  'documents.cards.status': 'Status',
  'documents.cards.fields': 'Fields',
  'documents.cards.confidence': 'Confidence',
  'documents.cards.children': 'Children',
  'documents.cards.date': 'Uploaded',
  'documents.cards.processedAt': 'Processed',

  // Detail panel
  'documents.detail.close': 'Close',
  'documents.detail.downloading': 'Downloading...',
  'documents.detail.downloadJson': 'Download JSON',
  'documents.detail.downloadXml': 'Download XML',
  'documents.detail.downloadPdf': 'Download PDF',
  'documents.detail.downloadFields': 'Download fields (CSV)',
  'documents.detail.type': 'Type',
  'documents.detail.origin': 'Origin',
  'documents.detail.status': 'Status',
  'documents.detail.avgConfidence': 'Average confidence',
  'documents.detail.processedAt': 'Processed',
  'documents.detail.processing': 'Processing',
  'documents.detail.processingStartedAt': 'Started',
  'documents.detail.importId': 'Import ID',
  'documents.detail.processingSteps': 'Processing steps',
  'documents.detail.needsReview': 'Needs review: {reason}',
  'documents.detail.reviewReasonDefault': 'Reason not specified',
  'documents.detail.error': 'Error: {error}',
  'documents.detail.extractedFields': 'Extracted fields',
  'documents.detail.col.field': 'Field',
  'documents.detail.col.value': 'Value',
  'documents.detail.col.confidence': 'Confidence',
  'documents.detail.col.origin': 'Origin',
  'documents.detail.col.raw': 'Raw',
  'documents.detail.metadata': 'Metadata',
  'documents.detail.children': 'Child documents ({count})',
  'documents.detail.childError': 'error',
  'documents.detail.processingLog': 'Processing audit log',

  // Editable cell tooltip
  'documents.cell.tooltip': '{field} — confidence {conf}% (double-click to edit)',

  // Provenance modal
  'documents.provenance.title': 'Chain of Origin',
  'documents.provenance.close': 'Close',
  'documents.provenance.noChain': 'No container_chain (direct terminal file).',
  'documents.delete': 'Delete',
  'documents.deleteConfirm': 'Are you sure you want to move this document to the trash? You can recover it for 30 days.',
  'documents.trash': 'Trash',
  'documents.restore': 'Restore',
  'documents.daysUntilPurge': 'Days until permanent deletion',
  'documents.timeRemaining': 'Time remaining: {days} days until permanent deletion',
  'documents.trashEmpty': 'The trash is empty.',
  'documents.trashDeletedAt': 'Deleted on',
  'documents.actions': 'Actions',
  'documents.error.delete': 'Error moving document to trash.',
  'documents.error.restore': 'Error restoring document.',

  // Batch selection (Feature 2)
  'documents.batch.selectedCount': '{count} selected',
  'documents.batch.selectAll': 'Select all',
  'documents.batch.deleteTitle': 'Delete documents',
  'documents.batch.deleteConfirm': 'Are you sure you want to delete {count} document(s)? They will be moved to the trash and can be recovered for 30 days.',
  'documents.batch.deletePartialError': 'Error deleting {failed} of {total} document(s).',

  // ---------------------------------------------------------------------------
  // upload — Upload.tsx
  // ---------------------------------------------------------------------------
  'upload.title': 'Document Upload',
  'upload.subtitle': 'Send PDFs, XMLs, emails (.eml/.msg), ZIPs, HTML or images for automatic extraction of tax fields.',
  'upload.dropzone.text': 'Drag files here or click to select',
  'upload.dropzone.formats': 'Formats: {formats} — Max 25 MB',
  'upload.process': 'Process ({count} pending)',
  'upload.processing': 'Processing...',
  'upload.clear': 'Clear',
  'upload.summary': '{done} completed, {errors} errors, {pending} pending',
  'upload.filesTitle': 'Uploaded files',
  'upload.viewResult': 'View result',
  'upload.remove': 'Remove',
  'upload.result.type': 'Type: {type}',
  'upload.result.origin': 'Origin: {origin}',
  'upload.result.fields': 'Fields: {count}',
  'upload.result.errors': 'Errors: {count}',
  'upload.result.duplicate': 'Duplicate',
  'upload.error.batchNotFound': 'Result not found in batch.',
  'upload.error.cancelledDuplicate': 'Cancelled (duplicate not processed)',
  'upload.error.timeout': 'Timed out waiting for processing.',
  // Issue #279: Batch mode for uploads of 3+ files.
  'upload.batch.submitted': 'Batch sent. You will be notified when it completes.',
  'upload.batch.inProgress': 'Batch in progress: {done}/{total} completed',
  'upload.batch.complete': 'Batch complete: {success} successes, {errors} errors',
  'upload.batch.blocked': 'Wait for the current batch to finish',
  'upload.batch.networkError': 'The batch may still be processing on the server. Check the documents list before resubmitting.',
  'upload.batch.checkStatus': 'Check status',

  // Status badge labels
  'upload.status.pending': 'Pending',
  'upload.status.uploading': 'Uploading...',
  'upload.status.processing': 'Processing...',
  'upload.status.done': 'Completed',
  'upload.status.error': 'Error',

  // Result panel
  'upload.result.title': 'Extraction result',
  'upload.result.close': 'Close',
  'upload.result.file': 'File',
  'upload.result.typeLabel': 'Type',
  'upload.result.originLabel': 'Origin',
  'upload.result.time': 'Time',
  'upload.result.needsReview': 'Needs review: {reason}',
  'upload.result.reviewReasonDefault': 'Reason not specified',
  'upload.result.error': 'Error: {error}',
  'upload.result.extractedFields': 'Extracted fields',
  'upload.result.col.field': 'Field',
  'upload.result.col.value': 'Value',
  'upload.result.col.confidence': 'Confidence',
  'upload.result.col.origin': 'Origin',
  'upload.result.metadata': 'Metadata',
  'upload.result.children': 'Child documents ({count})',
  'upload.result.childError': 'error',

  // Duplicate modal (single file)
  'upload.duplicate.title': 'Duplicate NF Detected',
  'upload.duplicate.intro': 'This NF has already been processed previously.',
  'upload.duplicate.type.file': 'Identical file',
  'upload.duplicate.type.data': 'Same NF (data)',
  'upload.duplicate.importId': 'Import ID',
  'upload.duplicate.type': 'Type',
  'upload.duplicate.file': 'File',
  'upload.duplicate.processedAt': 'Processed on',
  'upload.duplicate.whatToDo': 'What do you want to do?',
  'upload.duplicate.replace': 'Replace',
  'upload.duplicate.replaceDesc': 'removes the previous record and creates a new one',
  'upload.duplicate.archive': 'Archive',
  'upload.duplicate.archiveDesc': 'keeps the previous record archived and creates a new one',
  'upload.duplicate.duplicate': 'Duplicate',
  'upload.duplicate.duplicateDesc': 'creates a new record without changing the previous one',
  'upload.duplicate.cancel': 'Cancel',
  'upload.duplicate.cancelDesc': 'do not process',
  'upload.duplicate.processing': 'Processing...',

  // Batch duplicate modal
  'upload.batchDuplicate.title': '{count} duplicate file(s) found',
  'upload.batchDuplicate.intro': 'The files below have already been processed previously. Choose an action for each one. Non-duplicate files will be processed normally.',
  'upload.batchDuplicate.type.file': 'Identical file',
  'upload.batchDuplicate.type.data': 'Same NF (data)',
  'upload.batchDuplicate.existingImportId': 'Existing Import ID',
  'upload.batchDuplicate.existingFile': 'Existing file',
  'upload.batchDuplicate.processedAt': 'Processed on',
  'upload.batchDuplicate.replace': 'Replace',
  'upload.batchDuplicate.replaceDesc': 'removes the previous and creates new',
  'upload.batchDuplicate.archive': 'Archive',
  'upload.batchDuplicate.archiveDesc': 'archives the previous and creates new',
  'upload.batchDuplicate.duplicate': 'Duplicate',
  'upload.batchDuplicate.duplicateDesc': 'creates new without changing the previous',
  'upload.batchDuplicate.remove': 'Remove',
  'upload.batchDuplicate.removeDesc': 'deletes the previous record (admin)',
  'upload.batchDuplicate.cancel': 'Cancel',
  'upload.batchDuplicate.cancelDesc': 'do not process',
  'upload.batchDuplicate.cancelAll': 'Cancel all',
  'upload.batchDuplicate.confirm': 'Confirm decisions',
  'upload.batchDuplicate.processing': 'Processing...',

  // Notifications (Issue #281)
  'notifications.title': 'Notifications',
  'notifications.empty': 'No notifications',
  'notifications.markAllRead': 'Mark all as read',
  'notifications.batchComplete': 'Batch complete',
  'notifications.justNow': 'just now',
  'notifications.minutesAgo': '{n} min ago',
  'notifications.hoursAgo': '{n} h ago',
  'notifications.daysAgo': '{n} d ago',
  // Sprint 7: Billing / Pricing / Signup

  'layout.nav.pricing': 'Plans',
  'pricing.title': 'Choose the right plan for you',
  'pricing.subtitle': 'Start free and scale as you grow. No credit card required.',
  'pricing.unlimited': 'Contact us',
  'pricing.free': 'Free',
  'pricing.month': 'month',
  'pricing.unlimitedDocs': 'Unlimited documents',
  'pricing.docsPerMonth': '{count} documents/month',
  'pricing.popular': 'Most popular',
  'pricing.feat.upload': 'Document upload',
  'pricing.feat.exportCsv': 'Export CSV',
  'pricing.feat.multiUser': 'Multiple users',
  'pricing.feat.audit': 'Audit log',
  'pricing.feat.apiAccess': 'API access',
  'pricing.feat.prioritySupport': 'Priority support',
  'pricing.ctaFree': 'Start free',
  'pricing.ctaPaid': 'Subscribe',
  'pricing.processing': 'Processing...',
  'pricing.pixTitle': 'Pay via PIX',
  'pricing.pixInstructions': 'Scan the QR code below with your bank app to pay the subscription.',
  'pricing.goToBilling': 'Go to Billing',
  // Issue #544: discount coupon at checkout.
  'pricing.coupon.label': 'Have a coupon?',
  'pricing.coupon.placeholder': 'Enter coupon code',
  'pricing.coupon.apply': 'Apply',
  'pricing.coupon.applying': 'Applying...',
  'pricing.coupon.remove': 'Remove',
  'pricing.coupon.applied': 'Coupon applied!',
  // Security review #544: 'invalid' is the anti-enumeration response
  // from /validate (nonexistent or inactive — does not reveal existence).
  'pricing.coupon.error.invalid': 'Invalid coupon.',
  'pricing.coupon.error.not_found': 'Coupon not found.',
  'pricing.coupon.error.inactive': 'Coupon is inactive.',
  'pricing.coupon.error.expired': 'Coupon has expired.',
  'pricing.coupon.error.ineligible_plan': 'Coupon is not eligible for this plan.',
  'pricing.coupon.error.exhausted': 'Coupon exhausted (usage limit reached).',
  'pricing.coupon.error.org_exhausted': 'Coupon already used by this organization.',
  'pricing.coupon.error.invalid_plan': 'Invalid plan for coupon application.',
  'pricing.courtesyTitle': 'Plan activated!',
  'pricing.courtesyBody': 'Your coupon covered 100% of the price — no payment needed.',
  // Issue #593: Single/Team/Enterprise plans (#592 contract) + comparison
  // table on the same page.
  'pricing.ctaEnterprise': 'Talk to sales',
  'pricing.enterpriseMailSubject': 'Aura Nota — Enterprise plan',
  'pricing.users.one': '1 user',
  'pricing.users.upTo': 'Up to {count} users',
  'pricing.users.unlimited': 'Unlimited users',
  'pricing.feat.exportJson': 'Export JSON',
  'pricing.feat.emailCapture': 'Dedicated e-mail capture',
  'pricing.feat.accountant': 'Accountant user',
  'pricing.feat.onboarding': 'Assisted onboarding',
  'comparison.title': 'Plan comparison',
  'comparison.subtitle': 'Every feature, side by side.',
  'comparison.feature': 'Feature',
  'comparison.unlimited': 'Unlimited',
  'comparison.row.docs': 'Documents per month',
  'comparison.row.users': 'Included users',
  'comparison.row.accountant': 'Accountant user',
  'comparison.row.emailUpload': 'Dedicated e-mail capture and manual upload',
  'comparison.row.exportJson': 'JSON export',
  'comparison.row.exportCsv': 'CSV export',
  'comparison.row.multiUser': 'Multi-user (team)',
  'comparison.row.audit': 'Audit trail (LGPD)',
  'comparison.row.apiAccess': 'API access',
  'comparison.row.prioritySupport': 'Priority support',
  'comparison.row.onboarding': 'Assisted onboarding',
  'signup.title': 'Create account',
  'signup.subtitle': 'Start using Aura Nota for free.',
  'signup.name': 'Name',
  'signup.company': 'Company',
  'signup.email': 'Email',
  'signup.password': 'Password',
  'signup.passwordHint': 'Minimum 8 characters, 1 letter and 1 number.',
  'signup.submit': 'Create account',
  'signup.submitting': 'Creating...',
  'signup.error': 'Could not create the account.',
  'signup.haveAccount': 'Already have an account?',
  'signup.loginLink': 'Log in',
  'signup.successTitle': 'Account created!',
  'signup.successBody': 'Check your email to activate your account. After activation, you can log in.',
  'signup.goLogin': 'Go to login',
  'billing.title': 'Billing',
  'billing.currentPlan': 'Current plan',
  'billing.planLabel': 'Plan',
  'billing.statusLabel': 'Status',
  'billing.status.active': 'Active',
  'billing.status.pending_payment': 'Payment pending',
  'billing.status.suspended': 'Suspended',
  'billing.status.cancelled': 'Cancelled',
  'billing.renewsOn': 'Renews on',
  'billing.paymentMethod': 'Payment method',
  'billing.usage': 'Usage this month',
  'billing.used': '{used} of {limit} documents',
  'billing.remaining': '{count} remaining',
  'billing.unlimited': 'Unlimited',
  'billing.resetsOn': 'Resets on {date}',
  'billing.pendingPayment': 'Pending payment',
  'billing.invoiceAmount': 'R$ {amount}',
  'billing.dueDate': 'Due: {date}',
  'billing.pixTitle': 'Pay via PIX',
  'billing.pixInstructions': 'Scan the QR code with your bank. After payment, an admin will confirm and your plan will be activated.',
  'billing.invoiceId': 'Invoice: {id}',
  'billing.refresh': 'Refresh',
  'billing.upgradePro': 'Upgrade to Pro',
  // Issue #593: upgrade now goes through the plans page (Single/Team).
  'billing.viewPlans': 'See plans and upgrade',
  'billing.cancel': 'Cancel subscription',
  'billing.cancelConfirm': 'Are you sure you want to cancel your subscription?',
  'billing.processing': 'Processing...',
  'signup.consentPrefix': 'I have read and agree to the',
  'signup.consentAnd': 'and the',
  'signup.consentRequired': 'You must accept the Terms of Use and the Privacy Policy to create an account.',

  // ---------------------------------------------------------------------------
  // invite — Invite.tsx (Issue #411 — team invitation acceptance)
  // ---------------------------------------------------------------------------
  'invite.title': 'Accept Invitation',
  'invite.subtitle': 'You have been invited to join an organization on Aura Nota. Create your password to continue.',
  'invite.name': 'Name (optional)',
  'invite.namePlaceholder': 'Your name',
  'invite.password': 'Password',
  'invite.passwordHint': 'Minimum of 8 characters.',
  'invite.consent': 'I have read and agree to the Terms of Use and Privacy Policy.',
  'invite.submit': 'Accept Invitation',
  'invite.successTitle': 'Account created!',
  'invite.successMessage': 'Your account has been created and you have been added to the organization. Log in to continue.',
  'invite.goLogin': 'Go to login',
  'invite.invalidToken': 'Invalid invitation. Request a new one.',
  'invite.expiredToken': 'Invitation expired. Request a new one.',
  'invite.error': 'Error accepting invitation. Try again.',
  'invite.errorPassword': 'Password must be at least 8 characters.',
  'invite.errorConsent': 'You must accept the terms to continue.',


  // install — InstallApp.tsx (PWA) — Sprint 7 (#S7-045)
  'install.title': 'Install Aura Nota',
  'install.subtitle': 'Access it faster and get a native-app-like experience',
  'install.button': 'Install now',
  'install.installed': 'App already installed',
  'install.why_install': 'Why install?',
  'install.benefit_fast': 'Faster access with a home-screen icon',
  'install.benefit_fullscreen': 'Full screen without the browser bar',
  'install.benefit_notifications': 'Push notifications',
  'install.benefit_native': 'Native-app-like experience',
  'install.menu_label': 'How to install',
  'install.all_browsers_can_install': 'All browsers can install Aura Nota. The installation method varies by browser — some offer one-click install, others require manually adding a shortcut.',
  'install.help_button': 'How to install the app',
  'install.not_supported': 'Your browser does not support automatic installation. Follow the instructions below to add it manually.',
  'install.contact_support': 'If you have questions, please contact support.',
  'install.instructions_title': 'Instructions by browser',
  'install.instructions_intro': 'Choose the browser you are using and follow the steps.',
  'install.compatibility_note': 'Installation works on most modern browsers. The "Install now" button depends on browser support.',
  'install.back_home': 'Back to home',
  'install.chrome_android': 'Chrome (Android)',
  'install.chrome_android.step1': 'Open Aura Nota in Chrome on your phone.',
  'install.chrome_android.step2': 'Tap the menu (three dots ⋮) in the top right.',
  'install.chrome_android.step3': 'Tap "Install app" or "Add to Home screen" and confirm.',
  'install.chrome_desktop': 'Chrome (Desktop)',
  'install.chrome_desktop.step1': 'Open Aura Nota in Chrome on your computer.',
  'install.chrome_desktop.step2': 'Look for the install icon (⊕) in the address bar, or open the menu (⋮).',
  'install.chrome_desktop.step3': 'Click "Install Aura Nota" and confirm.',
  'install.edge_desktop': 'Edge (Desktop)',
  'install.edge_desktop.step1': 'Open Aura Nota in Microsoft Edge.',
  'install.edge_desktop.step2': 'Look for the install icon in the address bar, or open the menu (⋯).',
  'install.edge_desktop.step3': 'Go to "Apps" → "Install this site as an app" and confirm.',
  'install.safari_ios': 'Safari (iOS)',
  'install.safari_ios.step1': 'Open Aura Nota in Safari on your iPhone or iPad.',
  'install.safari_ios.step2': 'Tap the Share button (square with up arrow □↑).',
  'install.safari_ios.step3': 'Tap "Add to Home Screen" and confirm.',
  'install.safari_macos': 'Safari (macOS)',
  'install.safari_macos.step1': 'Open Aura Nota in Safari on your Mac.',
  'install.safari_macos.step2': 'In the top menu, click "File".',
  'install.safari_macos.step3': 'Select "Add to Dock" and confirm.',
  'install.firefox_android': 'Firefox (Android)',
  'install.firefox_android.step1': 'Open Aura Nota in Firefox on your phone.',
  'install.firefox_android.step2': 'Tap the menu (three dots ⋮).',
  'install.firefox_android.step3': 'Tap "Add to Home screen" and confirm.',
  'install.firefox_desktop': 'Firefox (Desktop)',
  'install.firefox_desktop.step1': 'Firefox for desktop does not install PWAs natively.',
  'install.firefox_desktop.step2': 'Drag the address bar icon to your desktop to create a shortcut.',
  'install.firefox_desktop.step3': 'Or use Bookmarks → More → "Add to home screen" to save a shortcut.',
  'install.firefox_desktop.note': 'Shortcuts open the site in the browser, without a native-app experience.',
  // team — Team.tsx (Issue #328)
  'team.title': 'Team & Members',
  'team.subtitle': 'Manage the members of your organization.',
  'team.loading': 'Loading members...',
  'team.empty': 'No members found.',
  'team.error.load': 'Failed to load members.',
  'team.saving': 'Saving...',
  'team.col.name': 'Name',
  'team.col.email': 'Email',
  'team.col.role': 'Role',
  'team.col.status': 'Status',
  'team.col.joined': 'Joined',
  'team.col.actions': 'Actions',
  'team.role.admin': 'Administrator',
  'team.role.member': 'Member',
  'team.role.contador': 'Accountant',
  'team.role.updated': 'Role updated.',
  'team.role.error': 'Failed to update role.',
  'team.status.active': 'Active',
  'team.status.invited': 'Invited',
  'team.status.disabled': 'Disabled',
  'team.invite.button': 'Invite member',
  'team.invite.modalTitle': 'Invite member',
  'team.invite.email': 'Email',
  'team.invite.role': 'Role',
  'team.invite.cancel': 'Cancel',
  'team.invite.send': 'Send invite',
  'team.invite.success': 'Invite sent successfully.',
  'team.invite.error': 'Failed to send invite.',
  'team.remove.button': 'Remove',
  'team.remove.confirmTitle': 'Remove member',
  'team.remove.confirmBody': 'Are you sure you want to remove {email} from the organization?',
  'team.remove.cancel': 'Cancel',
  'team.remove.confirm': 'Remove',
  'team.remove.success': 'Member removed.',
  'team.remove.error': 'Failed to remove member.',
  'team.invites.title': 'Pending Invitations',
  'team.invites.empty': 'No pending invitations.',
  'team.invites.col.sentAt': 'Sent on',
  'team.invites.col.expiresAt': 'Expires on',
  'team.invite.cancel.button': 'Remove',
  'team.invite.cancel.confirmTitle': 'Remove Invitation',
  'team.invite.cancel.confirmBody': 'Are you sure you want to remove this invitation? The invitee will not be able to accept it.',
  'team.invite.cancel.success': 'Invitation removed.',
  'team.invite.cancel.error': 'Failed to remove invitation.',
  'team.invite.resend.button': 'Resend',
  'team.invite.resend.success': 'Invitation resent successfully!',
  'team.invite.resend.error': 'Error resending invitation.',

  // admin — AdminPanel.tsx (Issue #332)
  'admin.title': 'Admin Panel',
  'admin.subtitle': 'Global view of organizations, users and metrics.',
  // Issue #550 (feat/audit-gestao): admin.tab.* keys removed — the audit
  // tab left AdminPanel (now its own page in the "Gestao" nav group).
  'admin.loading': 'Loading data...',
  'admin.error.load': 'Failed to load admin data.',
  'admin.role.super_user': 'Super User',
  'admin.role.admin': 'Administrator',
  'admin.role.member': 'Member',
  'admin.role.contador': 'Accountant',
  'admin.metric.totalDocs': 'Documents',
  'admin.metric.totalUsers': 'Users',
  'admin.metric.totalOrgs': 'Organizations',
  'admin.metric.totalOrgsHint': 'total',
  'admin.metric.activeOrgs': 'Active orgs',
  'admin.metric.errorRate': 'Error rate',
  'admin.metric.errors': 'errors',
  'admin.orgs.title': 'Organizations',
  'admin.orgs.empty': 'No organizations found.',
  'admin.orgs.col.name': 'Organization',
  'admin.orgs.col.docs': 'Docs',
  'admin.orgs.col.users': 'Users',
  'admin.orgs.col.status': 'Status',
  'admin.orgs.col.created': 'Created',
  'admin.users.title': 'Users',
  'admin.users.empty': 'No users found.',
  'admin.users.col.email': 'Email',
  'admin.users.col.org': 'Organization',
  'admin.users.col.role': 'Role',
  'admin.users.col.status': 'Status',
  'admin.users.col.created': 'Created',
  'admin.users.active': 'Active',
  'admin.users.disabled': 'Disabled',
  'admin.showTest': 'Show test data',
  'admin.testBadge': 'Test',

  'admin.orgs.col.actions': 'Actions',
  'admin.impersonate': 'Access as',
  // Issue #544: discount coupon management (super_user).
  'admin.coupons.title': 'Discount coupons',
  'admin.coupons.subtitle': 'Create and manage coupons applicable to paid plan subscriptions.',
  'admin.coupons.empty': 'No coupons created.',
  'admin.coupons.error.load': 'Failed to load coupons.',
  'admin.coupons.error.create': 'Failed to save coupon.',
  'admin.coupons.typePercent': 'Percentage (%)',
  'admin.coupons.typeFixed': 'Fixed amount (R$)',
  'admin.coupons.unlimited': 'Unlimited',
  'admin.coupons.allPlans': 'All paid',
  'admin.coupons.active': 'Active',
  'admin.coupons.inactive': 'Inactive',
  'admin.coupons.deactivate': 'Deactivate',
  'admin.coupons.col.code': 'Code',
  'admin.coupons.col.type': 'Type',
  'admin.coupons.col.value': 'Value',
  'admin.coupons.col.uses': 'Uses',
  'admin.coupons.col.expires': 'Expires',
  'admin.coupons.col.plans': 'Plans',
  'admin.coupons.col.status': 'Status',
  'admin.coupons.col.actions': 'Actions',
  'admin.coupons.form.code': 'Code',
  // Security review #544: min 8 chars — guide toward non-trivial codes.
  'admin.coupons.form.codeHint': 'Min. 8 characters — use a non-trivial code (e.g., CAMPAIGN-2026-X4K9).',
  'admin.coupons.form.discountType': 'Type',
  'admin.coupons.form.value': 'Value',
  'admin.coupons.form.maxUses': 'Max global uses',
  'admin.coupons.form.maxUsesPerOrg': 'Max uses per org',
  'admin.coupons.form.expiresAt': 'Expires at',
  'admin.coupons.form.eligiblePlans': 'Eligible plans (empty = all)',
  'admin.coupons.form.submit': 'Create coupon',
  'admin.coupons.form.saving': 'Saving...',
  'impersonation.banner': 'You are accessing as {name}',
  'impersonation.exit': 'Exit',
  'impersonation.exiting': 'Exiting...',
  'impersonation.confirmTitle': 'Access as customer',
  'impersonation.confirmBody': 'You will access the system as if you were customer "{name}". All actions will be audited.',
  'impersonation.cancel': 'Cancel',
  'impersonation.confirm': 'Access',
  'impersonation.starting': 'Starting...',
  'layout.nav.sefaz': 'Search by Key',
  'sefaz.title': 'Search NF-e by Access Key',
  'sefaz.tab.individual': 'Individual',
  'sefaz.tab.batch': 'Batch (Excel)',
  'sefaz.individual.label': 'Access Key (44 digits)',
  'sefaz.individual.invalid': 'Invalid key: must have 44 digits with valid check digit',
  'sefaz.individual.valid': 'Valid key',
  'sefaz.individual.search': 'Search',
  'sefaz.individual.result': 'Search result',
  'sefaz.individual.goToDocs': 'View document in Documents',
  'sefaz.status': 'Status',
  'sefaz.batch.downloadTemplate': 'Download Excel Template',
  'sefaz.batch.dropHere': 'Drag an .xlsx file here or click to select',
  'sefaz.batch.errorFormat': 'File must be .xlsx',
  'sefaz.batch.progress': 'Batch progress',
  'sefaz.batch.total': 'Total',
  'sefaz.batch.processed': 'Processed',
  'sefaz.batch.success': 'Success',
  'sefaz.batch.error': 'Errors',
  'sefaz.batch.status': 'Status',
  'sefaz.batch.goToDocs': 'View created documents',
  'sefaz.batch.history': 'Previous batches',
  'sefaz.batch.export': 'Export results',
  'layout.nav.sefazGroup': 'Tax / SEFAZ',
  'sefaz.nav.status': 'Service status',
  'sefaz.nav.consulta': 'Lookup by key',
  'sefaz.nav.dfe': 'DF-e distribution by NSU',
  'sefaz.nav.certs': 'Certificates',
  'sefaz.statusPage.title': 'SEFAZ service status',
  'sefaz.statusPage.ufLabel': 'State (UF)',
  'sefaz.statusPage.tab.nfe': 'NF-e',
  'sefaz.statusPage.tab.cte': 'CT-e',
  'sefaz.statusPage.tab.mdfe': 'MDF-e',
  'sefaz.statusPage.consult': 'Check',
  'sefaz.statusPage.cstat': 'cStat',
  'sefaz.statusPage.xmotivo': 'Reason',
  'sefaz.statusPage.uf': 'State (UF)',
  'sefaz.statusPage.timestamp': 'Timestamp',
  'sefaz.statusPage.rateLimited': 'Request limit exceeded. Try again in {s}s.',
  'sefaz.statusPage.unavailable': 'SEFAZ service is currently unavailable. Please try again shortly.',
  'sefaz.gated.title': 'Requires a configured A1 certificate',
  'sefaz.gated.body': 'This lookup requires an A1 digital certificate linked to the organization. Register it under Fiscal/SEFAZ > Certificates.',
  'sefaz.gated.cta': 'Go to Certificates',
  'sefaz.dfe.title': 'DF-e distribution by NSU',
  'sefaz.dfe.nsuLabel': 'Last NSU (15 digits)',
  'sefaz.certs.title': 'A1 certificate management',
  'sefaz.certs.badge': 'Coming soon',
  'sefaz.certs.body': 'Here you will be able to register and manage the organization’s A1 digital certificate, required for authenticated SEFAZ lookups (key lookup and DF-e distribution).',
  // Sprint 14 (issue #482): pagina real de custodia de certificados A1.
  'sefaz.certs.subtitle': 'A1 digital certificate (e-CNPJ) used for authenticated SEFAZ lookups.',
  'sefaz.certs.retry': 'Try again',
  'sefaz.certs.tab.detail': 'Certificate',
  'sefaz.certs.tab.history': 'History',
  'sefaz.certs.empty.title': 'No certificate registered',
  'sefaz.certs.empty.body': 'Upload the organization’s A1 digital certificate (.pfx/.p12) to enable authenticated SEFAZ lookups.',
  'sefaz.certs.empty.memberHint': 'The organization has no digital certificate registered yet. Ask an administrator to upload it.',
  'sefaz.certs.empty.cta': 'Upload certificate',
  'sefaz.certs.member.title': 'Organization digital certificate',
  'sefaz.certs.member.absent': 'No digital certificate registered.',
  'sefaz.certs.member.configured': 'Digital certificate configured and active.',
  'sefaz.certs.member.expiring': 'The digital certificate expires in {n} day(s).',
  'sefaz.certs.member.expired': 'The digital certificate has expired.',
  'sefaz.certs.member.hint': 'Certificate management is restricted to organization administrators.',
  'sefaz.certs.expiredBanner': 'The digital certificate has expired — SEFAZ lookups are unavailable until rotation.',
  'sefaz.certs.expiredCta': 'Rotate now',
  'sefaz.certs.meta.title': 'Custodied certificate',
  'sefaz.certs.meta.cn': 'Common name (CN)',
  'sefaz.certs.meta.issuer': 'Issuer',
  'sefaz.certs.meta.serial': 'Serial number',
  'sefaz.certs.meta.cnpj': 'CNPJ',
  'sefaz.certs.meta.validFrom': 'Valid from',
  'sefaz.certs.meta.validTo': 'Valid until',
  'sefaz.certs.meta.fingerprint': 'SHA-256 fingerprint',
  'sefaz.certs.meta.uploadedAt': 'Uploaded at',
  'sefaz.certs.meta.uploadedBy': 'Uploaded by',
  'sefaz.certs.meta.lastSession': 'Last MCP session',
  'sefaz.certs.status.ativo': 'Active',
  'sefaz.certs.status.ausente': 'No certificate',
  'sefaz.certs.status.vencendo': 'Expires in {n} days',
  'sefaz.certs.status.expirado': 'Expired',
  'sefaz.certs.status.pendente': 'Pending consent',
  'sefaz.certs.status.removido': 'Removed',
  'sefaz.certs.actions.rotate': 'Rotate certificate',
  'sefaz.certs.actions.remove': 'Remove certificate',
  'sefaz.certs.upload.titleCreate': 'Upload A1 certificate',
  'sefaz.certs.upload.titleRotate': 'Rotate A1 certificate',
  'sefaz.certs.upload.dropzone': 'Drag the certificate file here or click to select',
  'sefaz.certs.upload.formats': 'Accepted formats: .pfx or .p12 — maximum 64 KB',
  'sefaz.certs.upload.changeFile': 'click to change the file',
  'sefaz.certs.upload.fileErrorExt': 'Invalid file: upload a .pfx or .p12 (A1) certificate.',
  'sefaz.certs.upload.fileErrorSize': 'File exceeds the 64 KB limit.',
  'sefaz.certs.upload.passwordLabel': 'Certificate password',
  'sefaz.certs.upload.passwordHint': 'The password is used only to validate the certificate and is stored encrypted — never displayed or saved in the browser.',
  'sefaz.certs.upload.submitCreate': 'Validate and store',
  'sefaz.certs.upload.submitRotate': 'Validate and rotate',
  'sefaz.certs.upload.successCreate': 'Certificate uploaded and stored successfully.',
  'sefaz.certs.upload.successRotate': 'Certificate rotated successfully.',
  'sefaz.certs.consent.text': 'I authorize Aura Nota to custody the organization’s A1 digital certificate (CNPJ {cnpj}) in encrypted form, to use it exclusively for fiscal lookups on my behalf through transient sessions, and I acknowledge that I may revoke this authorization and remove the certificate at any time.',
  'sefaz.certs.consent.textNoCnpj': 'I authorize Aura Nota to custody the organization’s A1 digital certificate in encrypted form, to use it exclusively for fiscal lookups on my behalf through transient sessions, and I acknowledge that I may revoke this authorization and remove the certificate at any time.',
  'sefaz.certs.consent.linkLabel': 'Read the custody terms',
  'sefaz.certs.mismatch.title': 'CNPJ mismatch',
  'sefaz.certs.mismatch.body': 'The new certificate’s CNPJ does not match the organization’s CNPJ. Only confirm if the organization actually changed its CNPJ or if this certificate belongs to the correct holder.',
  'sefaz.certs.mismatch.confirm': 'Confirm mismatch and rotate',
  'sefaz.certs.remove.title': 'Remove certificate',
  'sefaz.certs.remove.impact': 'Impact: this organization’s fiscal lookups (SEFAZ/RFB) become unavailable until a new certificate is uploaded. The encrypted material is erased immediately and the MCP session is closed.',
  'sefaz.certs.remove.confirmLabel': 'I understand the impact and confirm removal of the custodied certificate.',
  'sefaz.certs.remove.confirm': 'Remove permanently',
  'sefaz.certs.remove.success': 'Certificate removed from custody.',
  'sefaz.certs.history.col.action': 'Action',
  'sefaz.certs.history.col.user': 'User',
  'sefaz.certs.history.col.timestamp': 'Timestamp',
  'sefaz.certs.history.col.ip': 'IP',
  'sefaz.certs.history.empty': 'No events recorded for this certificate.',
  'sefaz.certs.history.prev': 'Previous',
  'sefaz.certs.history.next': 'Next',
  'sefaz.certs.history.page': 'Page {page} of {pages}',
  'sefaz.certs.history.action.cert_upload': 'Certificate upload',
  'sefaz.certs.history.action.cert_view': 'Metadata view',
  'sefaz.certs.history.action.cert_rotate': 'Certificate rotation',
  'sefaz.certs.history.action.cert_delete': 'Certificate removal',
  'sefaz.certs.history.action.cert_use': 'Used in SEFAZ lookup',
  'sefaz.certs.history.action.cert_consent_grant': 'Consent recorded',
  'sefaz.certs.history.action.cert_consent_revoke': 'Consent revoked',
  'sefaz.certs.history.action.cert_session_open': 'MCP session opened',
  'sefaz.certs.error.invalidPfx': 'Incorrect certificate password or invalid file.',
  'sefaz.certs.error.invalidChain': 'Certificate is not A1 ICP-Brasil (e-CPF, A3 or self-signed are not accepted).',
  'sefaz.certs.error.expired': 'Certificate expired — upload a valid certificate.',
  'sefaz.certs.error.cnpjMismatch': 'The certificate’s CNPJ does not match the organization’s CNPJ.',
  'sefaz.certs.error.rateLimited': 'Too many attempts. Try again in {min} min.',
  'sefaz.certs.error.uploadDisabled': 'Certificate upload is disabled in this environment.',
  'sefaz.certs.error.noOrgCnpj': 'Register the organization’s CNPJ before uploading the certificate.',
  'sefaz.certs.error.pfxTooLarge': 'Certificate file exceeds the accepted limit.',
  'sefaz.certs.error.alreadyExists': 'A certificate is already custodied — use rotation to replace it.',
  'sefaz.certs.error.consentRequired': 'You must accept the custody terms to continue.',
  'sefaz.certs.error.confirmRequired': 'Confirm the operation to continue.',
  'sefaz.certs.error.unavailable': 'Fiscal lookup service unavailable. Try again shortly.',
  'sefaz.certs.error.forbidden': 'You do not have permission for this action.',
  'sefaz.certs.error.validation': 'Invalid certificate data.',
  'sefaz.certs.error.generic': 'Could not complete the operation.',

  // catalogo — collaborative municipality/provider catalog (S10-T06)
  'layout.nav.catalogo': 'Municipalities',
  'catalogo.title': 'Municipality Catalog',
  'catalogo.subtitle': 'Collaborative municipality → NFS-e provider mapping.',
  'catalogo.stats.municipios': 'Municipalities covered',
  'catalogo.stats.provedores': 'Supported providers',
  'catalogo.stats.ufs': 'States covered',
  'catalogo.stats.pendentes': 'Pending suggestions',
  'catalogo.table.title': 'Municipalities',
  'catalogo.table.ibge': 'IBGE',
  'catalogo.table.nome': 'Municipality',
  'catalogo.table.uf': 'State',
  'catalogo.table.provedor': 'Provider',
  'catalogo.table.agregador': 'Aggregator',
  'catalogo.table.empty': 'No municipalities found.',
  'catalogo.filter.uf': 'State',
  'catalogo.filter.provedor': 'Provider',
  'catalogo.filter.busca': 'Search municipality',
  'catalogo.export': 'Export JSON',
  'catalogo.import': 'Import JSON',
  'catalogo.import.result': 'Import: {created} created, {updated} updated ({total} items).',
  'catalogo.suggest.title': 'Suggest mapping',
  'catalogo.suggest.ibge': 'IBGE code',
  'catalogo.suggest.nome': 'Municipality',
  'catalogo.suggest.uf': 'State',
  'catalogo.suggest.provedor': 'Provider',
  'catalogo.suggest.agregador': 'Aggregator (optional)',
  'catalogo.suggest.justificativa': 'Justification (optional)',
  'catalogo.suggest.submit': 'Submit suggestion',
  'catalogo.suggest.success': 'Suggestion sent for moderation.',
  // Issue #559 → product decision: "Request a provider" page for org
  // roles (full catalog is super_user only; parceiro moderates the
  // suggestions queue on the new page).
  'layout.nav.requestProvider': 'Request a provider',
  'catalogo.request.subtitle': 'See the NFS-e providers supported today and request coverage for your organization municipality.',
  'catalogo.supported.notice': 'These are the NFS-e providers currently approved and the municipalities covered by each. If the provider for your municipality is not listed, send a request below.',
  'catalogo.table.justificativa': 'Justification',
  'catalogo.moderation.queue': 'Moderation queue',
  'catalogo.moderation.queueEmpty': 'No pending requests.',
  'catalogo.supported.line': 'Currently supported providers: {list}',
  'catalogo.supported.title': 'Supported municipalities and providers',
  'catalogo.status.supported': 'Supported',
  'catalogo.request.title': 'Request a provider',
  'catalogo.request.submit': 'Send request',
  'catalogo.sugestoes.mine': 'My requests',
  'catalogo.sugestoes.mineEmpty': 'No requests sent.',
  'catalogo.sugestoes.title': 'Suggestions',
  'catalogo.sugestoes.empty': 'No suggestions.',
  'catalogo.sugestoes.status.pendente': 'Pending',
  'catalogo.sugestoes.status.aprovada': 'Approved',
  'catalogo.sugestoes.status.rejeitada': 'Rejected',
  'catalogo.moderation.actions': 'Actions',
  'catalogo.moderation.approve': 'Approve',
  'catalogo.moderation.reject': 'Reject',
  'catalogo.moderation.rejectPrompt': 'Rejection reason (optional):',
  'catalogo.history.title': 'Catalog history',
  'catalogo.history.action': 'Action',
  'catalogo.history.action.create': 'Create',
  'catalogo.history.action.update': 'Update',
  'catalogo.history.action.delete': 'Delete',
  'catalogo.history.origem.admin': 'Direct edit',
  'catalogo.history.origem.sugestao': 'Approved suggestion',
  'catalogo.history.origem.import': 'Import',
  'catalogo.history.empty': 'No changes recorded.',
  'catalogo.error.generic': 'Could not complete the operation.',

  // ---------------------------------------------------------------------------
  // settings — TenantSettings.tsx (S10-T01, tenant admin panel)
  // ---------------------------------------------------------------------------
  'layout.nav.settings': 'Settings',
  'settings.title': 'Tenant Settings',
  'settings.subtitle': 'Organization admin panel: users, providers, email, retention and thresholds.',
  'settings.loading': 'Loading...',
  'settings.saving': 'Saving...',
  'settings.save': 'Save',
  'settings.cancel': 'Cancel',
  'settings.close': 'Close',
  'settings.edit': 'Edit',
  'settings.delete': 'Delete',
  'settings.error.load': 'Failed to load settings.',
  'settings.error.action': 'Failed to run the action.',
  'settings.tab.users': 'Users',
  'settings.tab.providers': 'Providers',
  'settings.tab.email': 'Email',
  'settings.tab.retention': 'Retention',
  'settings.tab.thresholds': 'Thresholds',

  // Roles (S10-T02 profiles)
  'settings.role.admin': 'Administrator',
  'settings.role.owner': 'Administrator',
  'settings.role.member': 'Member',
  'settings.role.contador': 'Accountant',
  'settings.role.parceiro': 'Partner',
  'settings.role.revisor': 'Reviewer',
  'settings.role.financeiro': 'Finance',
  'settings.role.operador': 'Operator',
  'settings.role.auditor': 'Auditor',
  'settings.role.super_user': 'Super User',

  // Tab: users
  'settings.users.subtitle': 'Manage organization users: invite, change roles, deactivate or reset passwords.',
  'settings.users.inviteButton': 'Invite user',
  'settings.users.inviteTitle': 'Invite user',
  'settings.users.sendInvite': 'Send invite',
  'settings.users.inviteSuccess': 'Invite sent successfully.',
  'settings.users.inviteError': 'Failed to send invite.',
  'settings.users.pendingInvites': 'Pending invites',
  'settings.users.resend': 'Resend',
  'settings.users.cancelInvite': 'Cancel',
  'settings.users.col.email': 'Email',
  'settings.users.col.role': 'Role',
  'settings.users.col.status': 'Status',
  'settings.users.col.joined': 'Since',
  'settings.users.col.actions': 'Actions',
  'settings.users.active': 'Active',
  'settings.users.disabled': 'Disabled',
  'settings.users.activate': 'Reactivate',
  'settings.users.deactivate': 'Deactivate',
  'settings.users.resetPassword': 'Reset password',
  'settings.users.remove': 'Remove',
  'settings.users.removeConfirm': 'Remove {email} from the organization?',
  'settings.users.resetConfirm': 'Reset the password of {email}? A temporary password will be generated.',
  'settings.users.resetTitle': 'Temporary password generated',
  'settings.users.resetBody': 'New temporary password for {email}. Share it through a secure channel — it will not be shown again:',

  // Tab: providers
  'settings.providers.subtitle': 'Catalog of municipalities and NFS-e providers (add/edit mappings).',
  'settings.providers.searchPlaceholder': 'Search municipality...',
  'settings.providers.addButton': 'Add provider',
  'settings.providers.addTitle': 'Add municipality/provider',
  'settings.providers.editTitle': 'Edit municipality/provider',
  'settings.providers.empty': 'No municipalities found.',
  'settings.providers.saved': 'Provider saved successfully.',
  'settings.providers.deleted': 'Provider removed.',
  'settings.providers.deleteConfirm': 'Remove the mapping for {name}?',
  'settings.providers.col.ibge': 'IBGE code',
  'settings.providers.col.name': 'Municipality',
  'settings.providers.col.uf': 'State',
  'settings.providers.col.provider': 'Provider',
  'settings.providers.col.aggregator': 'Aggregator',
  // Issue #547: global catalog — org admins can only suggest (no direct edit).
  'settings.providers.globalNotice': 'The provider catalog is global and managed by the platform — request new mappings here.',
  'settings.providers.suggestTitle': 'Request provider/municipality',
  'settings.providers.mySuggestions': "Your organization's suggestions",

  // Tab: email
  'settings.email.imapTitle': 'IMAP — email capture',
  'settings.email.imapSubtitle': 'Mailboxes monitored for automatic invoice capture.',
  'settings.email.addImap': 'Add IMAP',
  'settings.email.editImap': 'Edit IMAP',
  'settings.email.empty': 'No IMAP configurations registered.',
  'settings.email.saved': 'IMAP configuration saved.',
  'settings.email.deleted': 'IMAP configuration removed.',
  'settings.email.deleteConfirm': 'Remove the IMAP configuration for {host}?',
  'settings.email.test': 'Test',
  'settings.email.testing': 'Testing...',
  'settings.email.smtpTitle': 'SMTP — outbound email',
  'settings.email.smtpSubtitle': 'Organization SMTP server for outbound email (invites, notifications).',
  'settings.email.saveSmtp': 'Save SMTP',
  'settings.email.smtpSaved': 'SMTP configuration saved.',
  'settings.email.col.host': 'Server',
  'settings.email.col.port': 'Port',
  'settings.email.col.user': 'Username',
  'settings.email.col.password': 'Password',
  'settings.email.col.folder': 'Folder',
  'settings.email.col.from': 'Sender (From)',
  'settings.email.useTls': 'Use TLS',
  'settings.email.enabled': 'Enabled',
  'settings.email.passwordSet': 'password saved',
  'settings.email.keepPassword': 'leave empty to keep',
  // Issue #542: sender whitelist + explicit opt-out in the IMAP modal.
  'settings.email.allowedSenders': 'Allowed senders',
  'settings.email.allowedSendersHint':
    'One email per line. Use *@domain.com to allow an entire domain.',
  'settings.email.allowedSendersAuthHint':
    'The whitelist filters by the declared sender; authenticity verification (SPF/DKIM via Authentication-Results) depends on the mailbox provider.',
  'settings.email.acceptAnySender': 'Accept emails from any sender (not recommended)',
  'settings.email.acceptAnyWarning':
    'This mailbox accepts attachments from any sender — configuring the allowed senders list is recommended.',

  // Issue #555 — dedicated per-tenant capture (captura-<slug>-<token> address).
  'settings.email.captureTitle': 'Email capture',
  'settings.email.captureSubtitle':
    'Your organization’s dedicated address. Forward emails with NFS-e/PDF attachments to this address — documents are captured automatically into your account.',
  'settings.email.captureCopy': 'Copy',
  'settings.email.captureCopied': 'Copied!',
  'settings.email.captureCopyError': 'Could not copy — select the address manually.',
  'settings.email.captureRegenerate': 'Regenerate address',
  'settings.email.captureRegenerateHint':
    'Regenerating disables the current address immediately — emails sent to it will no longer be captured.',
  'settings.email.captureRegenerateConfirm':
    'Regenerate the capture address?\n\nThe current address ({address}) will stop working — emails sent to it will no longer be captured. This action cannot be undone.',
  'settings.email.captureRegenerated': 'Capture address regenerated.',
  // Issue #570 — dedicated capture sender allowlist.
  'settings.email.captureSendersTitle': 'Allowed senders',
  'settings.email.captureSendersHint':
    'Team members are always allowed. Add below other emails authorized to send to this address (individual addresses only — domains are not accepted).',
  'settings.email.captureSenderPlaceholder': 'email@company.com',
  'settings.email.captureSenderAdd': 'Add',
  'settings.email.captureSenderRemove': 'Remove',
  'settings.email.captureSenderAdded': 'Sender added to the allowed list.',
  'settings.email.captureSenderRemoved': 'Sender removed from the allowed list.',
  'settings.email.captureSendersRejectNote':
    'Emails from senders outside this list are rejected automatically, with no processing.',

  // Tab: retention
  'settings.retention.subtitle': 'Organization data retention period (LGPD compliance and fiscal storage).',
  'settings.retention.daysLabel': 'Data retention (days)',
  'settings.retention.hint': 'Between 30 and 3650 days. Default: 1825 days (5 years — fiscal storage).',
  // Issue #560 — notice: retention does not delete documents automatically
  'settings.retention.notice': 'When a document passes the retention period, organization administrators receive an alert for review. No document is deleted automatically.',
  'settings.retention.saved': 'Retention updated.',

  // Tab: thresholds
  'settings.thresholds.subtitle': 'Extraction validation thresholds: VLM confidence, auto-approve and review.',
  'settings.thresholds.vlm': 'VLM confidence (0–1)',
  'settings.thresholds.vlmHint': 'Minimum VLM model confidence; below it the fallback is used.',
  'settings.thresholds.autoApprove': 'Auto-approve (0–1)',
  'settings.thresholds.autoApproveHint': 'Minimum confidence to approve automatically without review.',
  'settings.thresholds.needsReview': 'Needs review (0–1)',
  'settings.thresholds.needsReviewHint': 'Below this confidence the document goes to the review queue.',
  'settings.thresholds.saved': 'Thresholds updated.',

  // ---------------------------------------------------------------------------
  // onboarding — assisted tenant wizard (S10-T07, US-S10-007)
  // ---------------------------------------------------------------------------
  'layout.nav.onboarding': 'Onboarding',
  'onboarding.title': 'Assisted onboarding',
  'onboarding.subtitle': 'Set up the organization step by step: email, municipalities, thresholds and an interface tour.',
  'onboarding.progress': '{done} of {total} steps',
  'onboarding.error.load': 'Could not load the onboarding checklist.',
  'onboarding.error.action': 'Could not complete the action.',
  'onboarding.step.email': 'Email',
  'onboarding.step.municipios': 'Municipalities',
  'onboarding.step.thresholds': 'Thresholds',
  'onboarding.step.tour': 'Guided tour',
  'onboarding.step.auto': 'detected',
  'onboarding.step.autoHint': 'Step already satisfied by the organization’s current configuration.',
  'onboarding.step.complete': 'Complete step',
  'onboarding.step.completeHint': 'A step can only be completed once the matching configuration is saved.',
  'onboarding.step.done': 'Step completed',
  'onboarding.nav.prev': 'Back',
  'onboarding.nav.next': 'Next',
  'onboarding.finish': 'Finish onboarding',
  'onboarding.help.title': 'Help',
  'onboarding.help.email': 'Set up an IMAP mailbox for automatic invoice capture and/or the outbound SMTP. After onboarding, manage everything under Settings → Email.',
  'onboarding.help.municipios': 'Select the municipalities whose NFS-e your organization receives. The selection comes from the collaborative municipality → provider catalog.',
  'onboarding.help.thresholds': 'Set the extraction validation thresholds. You can adjust them later under Settings → Thresholds.',
  'onboarding.help.tour': 'The tour shows the main areas of the interface. You can skip it and complete the step manually.',
  'onboarding.help.link.settingsEmail': 'Open Settings',
  'onboarding.help.link.settingsThresholds': 'Open Settings',
  'onboarding.help.link.catalogo': 'Open municipality catalog',
  'onboarding.email.title': 'Email (capture and sending)',
  'onboarding.email.subtitle': 'Set up the monitored IMAP mailbox and/or the organization SMTP server, with a connection test.',
  'onboarding.email.imapDetected': 'IMAP configured',
  'onboarding.email.smtpDetected': 'SMTP configured',
  'onboarding.email.imapLegend': 'IMAP — invoice capture',
  'onboarding.email.imapHint': 'Mailbox monitored for automatic capture of NFS-e/invoices.',
  'onboarding.email.smtpLegend': 'SMTP — outbound email',
  'onboarding.email.smtpHint': 'SMTP server for sending invites and notifications.',
  'onboarding.email.saveImap': 'Save and test IMAP',
  'onboarding.email.imapSaved': 'IMAP saved.',
  'onboarding.email.saveSmtp': 'Save SMTP',
  'onboarding.email.smtpSaved': 'SMTP saved.',
  'onboarding.email.testSmtp': 'Test SMTP connection',
  'onboarding.email.testOk': 'Connection test: {message}',
  'onboarding.email.testFail': 'Test failed: {error}',
  'onboarding.email.tip.host': 'Mail server address (e.g. imap.example.com or smtp.example.com).',
  'onboarding.email.tip.password': 'Mailbox password or app password. Stored encrypted.',
  'onboarding.email.tip.testSmtp': 'Save SMTP before testing — the test uses the persisted configuration.',
  'onboarding.municipios.title': 'Monitored municipalities',
  'onboarding.municipios.subtitle': 'Select from the catalog the municipalities your organization receives NFS-e from.',
  'onboarding.municipios.searchPlaceholder': 'Search municipality by name...',
  'onboarding.municipios.search': 'Search',
  'onboarding.municipios.searching': 'Searching...',
  'onboarding.municipios.empty': 'No municipality found in the catalog.',
  'onboarding.municipios.selected': 'Selected',
  'onboarding.municipios.add': 'Add',
  'onboarding.municipios.remove': 'Remove',
  'onboarding.municipios.count': '{count} municipality(ies) selected.',
  'onboarding.municipios.save': 'Save selection',
  'onboarding.municipios.update': 'Update selection',
  'onboarding.municipios.saved': 'Selection saved ({count} municipalities).',
  'onboarding.thresholds.title': 'Validation thresholds',
  'onboarding.thresholds.subtitle': 'Minimum VLM confidence, auto-approve and review queue — default values are prefilled.',
  'onboarding.thresholds.save': 'Save thresholds',
  'onboarding.thresholds.saved': 'Thresholds saved.',
  'onboarding.tour.title': 'Guided interface tour',
  'onboarding.tour.subtitle': 'An interactive walkthrough of the main areas of the system.',
  'onboarding.tour.start': 'Start tour',
  'onboarding.tour.next': 'Next',
  'onboarding.tour.prev': 'Previous',
  'onboarding.tour.skip': 'Skip tour',
  'onboarding.tour.finish': 'Finish',
  'onboarding.tour.counter': '{current}/{total}',
  'onboarding.tour.step.intro.title': 'Welcome to Aura Nota',
  'onboarding.tour.step.intro.body': 'This quick tour shows where the main areas are: upload, documents, catalog and settings.',
  'onboarding.tour.step.upload.title': 'Document upload',
  'onboarding.tour.step.upload.body': 'Send NFS-e PDFs, XMLs and invoice images here — extraction is automatic.',
  'onboarding.tour.step.documents.title': 'Documents',
  'onboarding.tour.step.documents.body': 'All captured documents live here, with extraction and review status.',
  'onboarding.tour.step.catalogo.title': 'Municipality catalog',
  'onboarding.tour.step.catalogo.body': 'Check municipality → NFS-e provider coverage and suggest new mappings.',
  'onboarding.tour.step.settings.title': 'Tenant settings',
  'onboarding.tour.step.settings.body': 'Manage users, providers, email, retention and thresholds for the organization.',
  'onboarding.tour.step.done.title': 'Tour finished',
  'onboarding.tour.step.done.body': 'Done! Complete the step to finish the onboarding checklist.',
  'onboarding.done.title': 'Onboarding complete!',
  'onboarding.done.body': 'The organization is configured and ready to capture documents.',
  'onboarding.done.notified': '{count} partner(s) notified of the completion.',
  'onboarding.done.goUpload': 'Send first document',
  'onboarding.done.goSettings': 'Open settings',
};
